import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	AfterViewInit,
	Component,
	DestroyRef,
	ElementRef,
	HostListener,
	inject,
	OnInit,
	signal,
	WritableSignal,
	ChangeDetectionStrategy,
	viewChild,
} from '@angular/core';
import { LayoutService } from '../../../@vex/services/layout.service';
import { Icons } from '../../util/Icons';
import { SessionService } from '../../services/session.service';
import { UntypedFormControl, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { fadeInUp400ms } from '../../../@vex/animations/fade-in-up.animation';
import { SnackbarService } from '../../services/snackbar.service';
import * as Hammer from 'hammerjs';
import { NumberUtil } from 'src/app/util/NumberUtil';
import { BreadCrumbService } from '../../../@vex/components/breadcrumbs/BreadCrumbService';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import { DayjsDateAdapter, MAT_DAYJS_DATE_ADAPTER_OPTIONS, MAT_DAYJS_MONTHYEAR_FORMATS } from '../../util/material-dayjs-adapter';
import { MatDialog } from '@angular/material/dialog';
import { Consumption, ConsumptionInfo, ConsumptionRequest, EnergyEfficiencyControllerService } from '@eadsysteme/energyefficiencyclient-v2';
import { TranslocoLocaleService } from '@jsverse/transloco-locale';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { KeycloakService } from 'src/app/auth/keycloak.service';
import { TenantUnit } from '@eadsysteme/ewsrealestateclient-v1';
import { OwnerService, RealEstate, RealEstateService, Resident, TenantUnitService } from '@eadsysteme/ewsrealestateclient-v2';
import { finalize } from 'rxjs/operators';
import { firstValueFrom, map, Observable, of, zip } from 'rxjs';
import { ConsumptionCalcService, EnergyEfficiencySettingService, RealEstateSetting } from '@eadsysteme/energyefficiencyclient-v1';
import { PermissionObject } from '@eadsysteme/ewsauthclient-v1';
import { saveAs } from 'file-saver';
import { RefreshUviDialogComponent } from './refresh-uvi-dialog.component';
import { DateUtil } from '../../util/DateUtil';
import { ActivatedRoute } from '@angular/router';
import { PageLayoutComponent } from '../../../@vex/components/page-layout/page-layout.component';
import { PageLayoutHeaderDirective } from '../../../@vex/components/page-layout/page-layout-header.directive';
import { BreadcrumbsComponent } from '../../../@vex/components/breadcrumbs/breadcrumbs.component';
import { PageLayoutContentDirective } from '../../../@vex/components/page-layout/page-layout-content.directive';
import { MatDivider } from '@angular/material/divider';
import { MatTooltip } from '@angular/material/tooltip';
import { TenantDetailCardsComponent } from '../tenant-detail-cards/tenant-detail-cards.component';
import { MatFormField, MatLabel } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { ChartDisplayComponent } from './v1/chart-display/chart-display.component';
import { MatIcon } from '@angular/material/icon';
import { MatTabGroup, MatTab, MatTabContent } from '@angular/material/tabs';
import { ChartDisplayPerEnergytypeComponent } from './chart-display-per-energytype/chart-display-per-energytype.component';
import { PercentDisplayPerEnergyTypeComponent } from './percent-display-per-energytype/percent-display-per-energy-type.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatButton, MatIconButton } from '@angular/material/button';
import { DatePipe } from '@angular/common';
import PermissionTypeEnum = PermissionObject.PermissionTypeEnum;
import CalculationVersionTypeEnum = RealEstateSetting.CalculationVersionTypeEnum;
import SelectedVariantsEnum = RealEstateSetting.SelectedVariantsEnum;
import CostTypeEnum = Consumption.CostTypeEnum;

/** Monate ohne fertigen Report bleiben in der Auswahl sichtbar, sind aber nicht wählbar. */
export interface UviDateOption {
	date: Date;
	hasReport: boolean;
}

@Component({
	selector: 'app-uvi',
	templateUrl: './uvi.component.html',
	styleUrls: ['./uvi.component.scss'],
	animations: [fadeInUp400ms],
	providers: [
		{
			provide: DateAdapter,
			useClass: DayjsDateAdapter,
			deps: [MAT_DATE_LOCALE, MAT_DAYJS_DATE_ADAPTER_OPTIONS],
		},
		[NumberUtil],
		{ provide: MAT_DATE_FORMATS, useValue: MAT_DAYJS_MONTHYEAR_FORMATS },
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		TranslocoDirective,
		PageLayoutComponent,
		PageLayoutHeaderDirective,
		BreadcrumbsComponent,
		PageLayoutContentDirective,
		MatFormField,
		MatLabel,
		MatSelect,
		ReactiveFormsModule,
		FormsModule,
		MatOption,
		ChartDisplayComponent,
		MatIcon,
		MatTabGroup,
		MatTab,
		MatTabContent,
		ChartDisplayPerEnergytypeComponent,
		PercentDisplayPerEnergyTypeComponent,
		MatProgressSpinner,
		MatButton,
		MatIconButton,
		MatTooltip,
		MatDivider,
		TenantDetailCardsComponent,
		DatePipe,
	],
})
export class UviComponent implements OnInit, AfterViewInit {
	layoutService = inject(LayoutService);
	sessionService = inject(SessionService);
	private translocoLocaleService = inject(TranslocoLocaleService);
	private translocoService = inject(TranslocoService);
	dialog = inject(MatDialog);
	private snackbarService = inject(SnackbarService);
	icons = inject(Icons);
	private keycloakService = inject(KeycloakService);
	private realEstateService = inject(RealEstateService);
	private consumptionCalcService = inject(ConsumptionCalcService);
	private ownerService = inject(OwnerService);
	private tenantUnitService = inject(TenantUnitService);
	private energyEfficiencySettingsService = inject(EnergyEfficiencySettingService);
	private energyEfficiencyController = inject(EnergyEfficiencyControllerService);
	dateUtil = inject(DateUtil);
	private activatedRoute = inject(ActivatedRoute);
	breadCrumbService = inject(BreadCrumbService);

	private destroyRef = inject(DestroyRef);

	private readonly swipeWrapper = viewChild<ElementRef<HTMLElement>>('swipeWrapper');

	date = new UntypedFormControl(dayjs());
	readonly dateOptions = signal<UviDateOption[]>([]);
	model: { selectedDate: Date | null } = { selectedDate: null };
	locale: string;
	public calculationVersionType: CalculationVersionTypeEnum = CalculationVersionTypeEnum.V1;
	protected readonly CalculationVersionTypeEnum = CalculationVersionTypeEnum;
	protected currentRealEstate: WritableSignal<RealEstate> = signal(undefined);
	protected currentTenantUnit: WritableSignal<TenantUnit> = signal(undefined);
	private currentResident: Resident;
	private currentArea: number;
	showChart = signal(true);
	showPercentutal = signal(false);
	finishedLoading = signal(false);
	readonly noConsumptionsForMonth = signal(false);
	consumptions: Array<Consumption>;

	constructor() {
		const translocoLocaleService = this.translocoLocaleService;

		this.locale = translocoLocaleService.getLocale();
		this.currentArea = Number(sessionStorage.getItem('selectedRealEstateArea') ?? this.sessionService.effectiveArea);
	}

	ngOnInit() {
		const realEstateId = this.activatedRoute.snapshot.paramMap.get('realEstateId') ?? sessionStorage.getItem('selectedRealEstateID');
		const tenantUnitId = this.activatedRoute.snapshot.paramMap.get('tenantUnitId') ?? sessionStorage.getItem('selectedTenantUnitID');
		if (!realEstateId || !tenantUnitId) {
			this.snackbarService.error(this.translocoService.translate('errors.pleaseTryAgain'));
			return;
		}
		zip(
			this.realEstateService.getRealEstate(this.currentArea, realEstateId),
			this.tenantUnitService.getTenantUnitById(this.currentArea, realEstateId, tenantUnitId)
		)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((response) => {
				this.currentRealEstate.set(response[0]);
				this.currentTenantUnit.set(response[1]);

				this.initDateSelect();
				this.loadSettings();
			});
	}

	getCalculatedVersion(): Promise<ConsumptionInfo> {
		if (this.model.selectedDate) {
			const lastDayOfSelectedMonth = dayjs(this.model.selectedDate).endOf('month');
			const conReq: ConsumptionRequest = { readingDate: lastDayOfSelectedMonth.toISOString() };
			if (this.currentArea && this.currentTenantUnit()) {
				return this.energyEfficiencyController.getCalculationVersion(this.currentArea, this.currentTenantUnit().id, conReq).toPromise();
			}
		}
	}

	@HostListener('document:keydown', ['$event'])
	handleKeyboardEvent(event: KeyboardEvent) {
		this.switchDateByKey(event);
	}

	switchDateByKey(event) {
		if (event.key === 'ArrowLeft') {
			this.toPreviousMonth(event);
		} else if (event.key === 'ArrowRight') {
			this.toNextMonth(event);
		}
	}

	ngAfterViewInit(): void {
		const el = this.swipeWrapper()?.nativeElement;
		if (!el) {
			return;
		}
		// Angular 22 removed Hammer integration from @angular/platform-browser
		// (HAMMER_GESTURE_CONFIG); wire hammerjs directly for the month swipe gesture.
		const hammer = new Hammer.Manager(el, {
			recognizers: [[Hammer.Swipe, { direction: Hammer.DIRECTION_HORIZONTAL }]],
		});
		hammer.on('swipeleft', (event) => this.toNextMonth(event));
		hammer.on('swiperight', (event) => this.toPreviousMonth(event));
	}

	toNextMonth(event) {
		if (event.type === 'swipeleft') {
			if (!this.layoutService.isMobile()) {
				return;
			}
		}
		this.moveSelection(-1);
	}

	toPreviousMonth(event) {
		if (event.type === 'swiperight') {
			if (!this.layoutService.isMobile()) {
				return;
			}
		}
		this.moveSelection(1);
	}

	/** Überspringt Monate ohne Report. Die Liste ist absteigend sortiert, der jüngste Monat steht vorne. */
	private moveSelection(step: number) {
		const options = this.dateOptions();
		const index = options.findIndex((option) => option.date === this.model.selectedDate);
		if (index === -1) {
			return;
		}
		for (let next = index + step; next >= 0 && next < options.length; next += step) {
			if (options[next].hasReport) {
				this.model.selectedDate = options[next].date;
				this.dateChanged();
				return;
			}
		}
	}

	dateChanged() {
		this.finishedLoading.set(false);
		this.loadSettings();
		this.loadData();
	}

	refreshUvi() {
		this.dialog
			.open(RefreshUviDialogComponent, {
				data: { readingDate: this.model.selectedDate },
				autoFocus: false,
				restoreFocus: false,
			})
			.afterClosed()
			.subscribe((value) => {
				if (value === 'realEstate') {
					this.refreshRealEstate();
				} else if (value === 'tenantUnit') {
					this.refreshTenantUnit().finally(() => this.loadData());
				} else {
					return;
				}
			});
	}

	private loadSettings() {
		this.energyEfficiencySettingsService.getRealEstateSetting(this.currentArea, this.currentRealEstate().id).subscribe((rESettings) => {
			if (Array.from(rESettings.selectedVariants).length > 0) {
				this.showChart.set(rESettings.selectedVariants ? Array.from(rESettings.selectedVariants).includes(SelectedVariantsEnum.GRAPHICAL) : true); //chart as default
				this.showPercentutal.set(rESettings.selectedVariants ? Array.from(rESettings.selectedVariants).includes(SelectedVariantsEnum.TABULAR) : false);
			} else {
				this.energyEfficiencySettingsService.getServiceCompanySetting(this.currentArea).subscribe((sCSettings) => {
					this.showChart.set(sCSettings.selectedVariants ? Array.from(sCSettings.selectedVariants).includes(SelectedVariantsEnum.GRAPHICAL) : true); //chart as default
					this.showPercentutal.set(
						sCSettings.selectedVariants ? Array.from(sCSettings.selectedVariants).includes(SelectedVariantsEnum.TABULAR) : false
					);
				});
			}
		});
	}

	private initDateSelect() {
		this.resolveReportRangeEnd()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((moveOutDate) => this.getReportDates(moveOutDate));
	}

	/**
	 * Auszugsdatum, nach dem die fertigen Reports gefiltert werden. Für Eigentümer zählt der frühere
	 * der beiden validTo-Werte von Bewohner und Eigentümer, für alle anderen der des Bewohners.
	 * Ohne Wert bleibt der Parameter weg - ein leerer String wäre ein leerer Query-Parameter und
	 * damit etwas anderes als "nicht gesetzt".
	 */
	private resolveReportRangeEnd(): Observable<string | undefined> {
		const residentValidTo = this.currentResident?.validTo ?? this.currentTenantUnit()?.currentResident?.validTo;
		const rolePermission = this.sessionService.selectedTenantUnitRolePermission;
		if (rolePermission?.permissionType !== PermissionTypeEnum.OWNER) {
			return of(residentValidTo);
		}
		return this.ownerService
			.getOwner(this.currentArea, this.currentRealEstate().id, this.currentTenantUnit().id, rolePermission.permissionUserId)
			.pipe(map((owner) => this.earlierDate(residentValidTo, owner?.validTo)));
	}

	private earlierDate(left?: string, right?: string): string | undefined {
		if (!left || !right) {
			return left ?? right;
		}
		return dayjs(left).isBefore(dayjs(right)) ? left : right;
	}

	private getReportDates(moveOutDate?: string) {
		this.consumptionCalcService
			.getFinishedReportDates(this.currentArea, this.currentRealEstate().id, this.currentTenantUnit().id, moveOutDate)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((readingDates) => this.applyDateOptions(readingDates ?? []));
	}

	/**
	 * Wählbar sind die Monate, für die das Backend einen fertigen Report meldet. Dev-Rollen sehen
	 * zusätzlich die letzten 13 Monate, damit beim Testen sichtbar bleibt, wo Daten fehlen - die
	 * bleiben deaktiviert, weil ein Abruf darauf nur in einer Fehlermeldung enden würde.
	 *
	 * Meldet das Backend gar keine Reports, wissen wir nichts über die Datenlage. Dann darf die
	 * Auswahl nicht leer bleiben, sonst ist die Seite tot: die angebotenen Monate sind wählbar und
	 * der Leerzustand pro Monat übernimmt.
	 */
	private applyDateOptions(readingDates: string[]) {
		const optionsByMonth = new Map<string, UviDateOption>();
		for (const readingDate of readingDates) {
			const date = new Date(readingDate);
			optionsByMonth.set(dayjs(date).format('YYYY-MM'), { date, hasReport: true });
		}
		const backendKnowsReports = optionsByMonth.size > 0;
		for (const date of this.additionalMonths(backendKnowsReports)) {
			const monthKey = dayjs(date).format('YYYY-MM');
			if (!optionsByMonth.has(monthKey)) {
				optionsByMonth.set(monthKey, { date, hasReport: !backendKnowsReports });
			}
		}
		this.dateOptions.set([...optionsByMonth.values()].sort((left, right) => right.date.getTime() - left.date.getTime()));
		this.setCurrentMonth();
		this.loadData();
	}

	private additionalMonths(backendKnowsReports: boolean): Date[] {
		if (this.showsMonthsWithoutReport()) {
			return Array.from({ length: 13 }, (unused, index) =>
				dayjs()
					.subtract(index + 1, 'month')
					.toDate()
			);
		}
		return backendKnowsReports ? [] : [dayjs().subtract(1, 'month').endOf('month').toDate()];
	}

	private showsMonthsWithoutReport(): boolean {
		return this.keycloakService.getKeycloakInstance().hasRealmRole('dev');
	}

	/** Ohne Monat mit Report bleibt die Auswahl leer, statt auf ein Datum zu zeigen, das im Select fehlt. */
	private setCurrentMonth() {
		this.model.selectedDate = this.dateOptions().find((option) => option.hasReport)?.date ?? null;
	}

	hasConsumptionData(): boolean {
		return !!this.model.selectedDate && !this.noConsumptionsForMonth();
	}

	emptyStateKey(): string {
		return this.model.selectedDate ? 'errors.noConsumptions' : 'uvi.noUviExists';
	}

	printUvi() {
		this.performPrint(true);
	}

	private async loadData() {
		this.noConsumptionsForMonth.set(false);
		if (!this.model.selectedDate) {
			this.finishedLoading.set(true);
			return;
		}
		const lastDayOfSelectedMonth = dayjs(this.model.selectedDate).endOf('month');
		const consumptionInfo: ConsumptionInfo = await this.getCalculatedVersion();
		if (!consumptionInfo?.calculated) {
			this.noConsumptionsForMonth.set(true);
			this.finishedLoading.set(true);
			return;
		}
		this.calculationVersionType = consumptionInfo.calculationVersionType;

		const conReq: ConsumptionRequest = { readingDate: lastDayOfSelectedMonth.toISOString() };
		if (this.CalculationVersionTypeEnum.V2 === this.calculationVersionType) {
			this.energyEfficiencyController
				.calculateEnergyEfficiency(this.currentArea, this.currentRealEstate().id, this.currentTenantUnit().id, conReq)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.pipe(finalize(() => this.finishedLoading.set(true)))
				.subscribe((consumptions) => (this.consumptions = consumptions));
		} else {
			this.finishedLoading.set(true);
		}
	}

	performPrint(billable: boolean) {
		try {
			const printDate = dayjs(this.model.selectedDate).isAfter(dayjs()) ? dayjs() : dayjs(this.model.selectedDate);
			this.consumptionCalcService
				.getConsumptionReport(this.currentArea, this.currentRealEstate().id, this.currentTenantUnit().id, billable, {
					readingDate: printDate.format('YYYY-MM-DD'),
				})
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe((report) => {
					if (report) {
						const blob = new Blob([report], { type: 'application/pdf' });
						const fileName = this.setFileName();
						saveAs(blob, fileName);
					}
				});
		} catch (exception) {
			if (this.sessionService.isDevRole()) {
				console.log('an error occurred while generating pdf: ' + exception.toString());
			}
		}
	}

	setFileName(): string {
		const activeLang = this.translocoService.getActiveLang();
		const cIY = activeLang === 'en' ? 'cIY' : 'uVI';
		const month = '_' + dayjs(this.model.selectedDate).locale(activeLang).format('MMMM');
		const year = '_' + dayjs(this.model.selectedDate).year();
		return cIY + month + year + '.pdf';
	}

	private async refreshTenantUnit() {
		if (!this.currentRealEstate() && !this.currentTenantUnit()) {
			return;
		}
		const consumptionRequest: ConsumptionRequest = {};
		consumptionRequest.readingDate = dayjs(this.model.selectedDate).format('YYYY-MM-DD');
		this.finishedLoading.set(false);
		await firstValueFrom(
			this.consumptionCalcService.resetTenantUnitEnergyEfficiency(this.currentRealEstate().id, this.currentTenantUnit().id, consumptionRequest)
		).then(() => this.finishedLoading.set(true));
	}

	private refreshRealEstate() {
		if (!this.currentRealEstate().id) {
			return;
		}
		const consumptionRequest: ConsumptionRequest = {};
		consumptionRequest.readingDate = dayjs(this.model.selectedDate).format('YYYY-MM-DD');
		this.consumptionCalcService
			.resetRealEstateEnergyEfficiency(this.currentRealEstate().id, consumptionRequest)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: () => this.snackbarService.success(this.translocoService.translate('uvi.refreshedRealEstate')),
			});
	}

	isRoleSupport() {
		return this.sessionService.isSupportRole();
	}

	getIconByCostType(costType: CostTypeEnum) {
		switch (costType) {
			case CostTypeEnum.HEATING:
				return this.icons.get('heating');
			case CostTypeEnum.WARMWATER:
				return this.icons.get('warmwater');
			case CostTypeEnum.COLDWATER:
				return this.icons.get('coldwater');
			case CostTypeEnum.COOLING:
				return this.icons.get('cooling');
			case CostTypeEnum.HEATING_WARMWATER:
				return this.icons.get('heatingWarmwater');
		}
	}

	getLabelByCostType(costType: CostTypeEnum) {
		switch (costType) {
			case CostTypeEnum.HEATING:
				return this.translocoService.translate('uvi.heating');
			case CostTypeEnum.WARMWATER:
				return this.translocoService.translate('uvi.warmwater');
			case CostTypeEnum.COLDWATER:
				return this.translocoService.translate('uvi.coldwater');
			case CostTypeEnum.COOLING:
				return this.translocoService.translate('uvi.cooling');
			case CostTypeEnum.HEATING_WARMWATER:
				return this.translocoService.translate('uvi.heatingWarmwater');
		}
	}
}
